/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// SUPABASE ENVIRONMENT CONFIGURATION
// ============================================================================

export interface SupabaseConfig {
  url: string;
  anonKey: string;
  serviceRoleKey: string;
  environment: "local" | "production";
}

/**
 * Get client-safe Supabase configuration
 * This excludes service role key and is safe for client-side usage
 */
export function getSupabaseConfig(): SupabaseConfig {
  // Only use NEXT_PUBLIC_ variables for client safety
  const isTest = process.env.NODE_ENV === "test";
  const isBuild =
    process.env.NODE_ENV === "production" &&
    process.env["NEXT_PHASE"] === "phase-production-build";
  const isDev = process.env.NODE_ENV === "development";

  // During build/dev compilation phase, provide safe defaults to prevent compilation failures
  // This prevents Next.js 16 canary from hanging during compilation
  const url =
    process.env["NEXT_PUBLIC_SUPABASE_URL"] ??
    (isTest
      ? "http://localhost:54321"
      : isBuild || isDev
        ? "https://build-placeholder.supabase.co"
        : undefined);

  const anonKey =
    process.env["NEXT_PUBLIC_SUPABASE_ANON_KEY"] ??
    (isTest
      ? "test_anon_key"
      : isBuild || isDev
        ? "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJidWlsZC1wbGFjZWhvbGRlciIsInJvbGUiOiJhbm9uIiwiZXhwIjoxOTgzODEyOTk2fQ.build-placeholder-key"
        : undefined);

  // Determine environment based on URL
  const environment =
    url?.includes("127.0.0.1") || url?.includes("localhost")
      ? "local"
      : "production";

  // During dev compilation, allow placeholder values to prevent compilation hangs
  // Runtime will validate and throw if actually missing
  if (!url || !anonKey) {
    const context = isBuild || isDev ? "compilation-time" : "runtime";
    // Only throw at runtime, not during compilation
    if (!isBuild && !isDev) {
      throw new Error(
        `Missing required Supabase environment variables at ${context}: NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY`,
      );
    }
    // During compilation, use placeholders to prevent blocking
    return {
      url: url ?? "https://compilation-placeholder.supabase.co",
      anonKey: anonKey ?? "compilation-placeholder-key",
      serviceRoleKey: "",
      environment: "production" as const,
    };
  }

  // Validate URL format (skip validation for build placeholders)
  if (!isBuild || !url.includes("build-placeholder")) {
    try {
      new URL(url);
    } catch {
      throw new Error("Invalid Supabase URL format");
    }
  }

  // Service role key is server-only; intentionally blank on the client to avoid bundling secrets.
  const serviceRoleKey =
    typeof window === "undefined"
      ? (process.env["SUPABASE_SERVICE_ROLE_KEY"] ?? "")
      : "";

  return {
    url,
    anonKey,
    serviceRoleKey,
    environment,
  };
}

/**
 * Check if we're using local development
 */
export function isLocalDevelopment(): boolean {
  return getSupabaseConfig().environment === "local";
}

/**
 * Check if we're using production
 */
export function isProduction(): boolean {
  return getSupabaseConfig().environment === "production";
}

/**
 * Get the current Supabase URL for logging/debugging
 */
export function getCurrentSupabaseUrl(): string {
  return getSupabaseConfig().url;
}

/**
 * Validate environment variables are properly set
 * Updated to be environment-aware (client vs server)
 */
export function validateEnvironment(): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Client-side variables (always required)
  if (!process.env["NEXT_PUBLIC_SUPABASE_URL"]) {
    errors.push("NEXT_PUBLIC_SUPABASE_URL is required");
  }

  if (!process.env["NEXT_PUBLIC_SUPABASE_ANON_KEY"]) {
    errors.push("NEXT_PUBLIC_SUPABASE_ANON_KEY is required");
  }

  if (!process.env["NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY"]) {
    errors.push("NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY is required");
  }

  // Server-side variables (only validate on server)
  // Note: Service role key validation moved to server-config.ts
  if (typeof window === "undefined") {
    if (!process.env["CLERK_SECRET_KEY"]) {
      errors.push("CLERK_SECRET_KEY is required");
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Validate only what a service-role Supabase client needs. The admin client
 * never touches Clerk, so missing Clerk keys must not break server-side
 * writes made with it (cron, webhooks, atomic RPCs, test/CI harnesses). The
 * service role key itself is checked by the factory via getSupabaseConfig().
 */
export function validateServiceRoleEnvironment(): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  if (!process.env["NEXT_PUBLIC_SUPABASE_URL"]) {
    errors.push("NEXT_PUBLIC_SUPABASE_URL is required");
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Validate client-side environment variables only
 * Safe to call from browser contexts
 */
export function validateClientEnvironment(): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!process.env["NEXT_PUBLIC_SUPABASE_URL"]) {
    errors.push("NEXT_PUBLIC_SUPABASE_URL is required");
  }

  if (!process.env["NEXT_PUBLIC_SUPABASE_ANON_KEY"]) {
    errors.push("NEXT_PUBLIC_SUPABASE_ANON_KEY is required");
  }

  if (!process.env["NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY"]) {
    errors.push("NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY is required");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
